import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { useScenarioStore } from '../../pages/scenario-modeling/scenario-modeling.store';
import { ApiService } from '../../services/index';
import {
  LiquidityCapTableByStakeholderType,
  ScenarioCapTableByStakeholderType,
  ScenarioType,
} from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export const useCapTableStakeholderTypes = <T extends ScenarioType = ScenarioType.FUNDRAISING>({
  scenarioId,
}: {
  scenarioId: string;
}) => {
  const queryClient = useQueryClient();
  const { search } = useScenarioStore();

  const { data, isLoading, isSuccess } = useAppQuery<
    (T extends ScenarioType.FUNDRAISING
      ? ScenarioCapTableByStakeholderType
      : LiquidityCapTableByStakeholderType)[]
  >({
    queryKey: [QueryKey.CAP_TABLE_STAKEHOLDER_TYPES, scenarioId, search],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.SCENARIOS,
          routePath: [scenarioId, 'cap-table', 'stakeholder-types'],
        },
        { queryParams: { search } },
      ),
    refetchOnWindowFocus: false,
    // Tab remounts must render cached rows instantly — see use-cap-table-stakeholder-by-all.
    staleTime: 30_000,
    enabled: !!scenarioId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CAP_TABLE_STAKEHOLDER_TYPES] });
  }, [queryClient]);

  return {
    invalidateQuery,
    data,
    isSuccess,
    isLoading,
    isEmptySearch: Boolean(search && !data?.length),
  };
};
